<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

const props = defineProps({
  content: { type: String, default: '' },
  contentAttributes: { type: Object, default: () => ({}) },
  variant: {
    type: String,
    default: 'default',
    validator: value => ['default', 'widget'].includes(value),
  },
});

const { t, te, locale } = useI18n();
const { formatMessage } = useMessageFormatter();

const translate = key => {
  const dashboardKey = `CONVERSATION.REFUND_RECEIPT.${key}`;
  const widgetKey = `REFUND_RECEIPT.${key}`;

  return te(dashboardKey) ? t(dashboardKey) : t(widgetKey);
};

const pick = (...keys) => {
  const attributes = props.contentAttributes || {};
  const key = keys.find(item => attributes[item] !== undefined);
  return key ? attributes[key] : undefined;
};

const pickFrom = (source, ...keys) => {
  const key = keys.find(item => source[item] !== undefined);
  return key ? source[key] : undefined;
};

const isPresent = value =>
  value !== undefined && value !== null && value !== '';

const formatMoney = value => {
  if (!isPresent(value)) return '';
  const currency = pick('currency') || 'USD';
  const numericAmount = Number(value);

  if (Number.isNaN(numericAmount)) return `${value} ${currency}`;

  try {
    return new Intl.NumberFormat(locale.value, {
      style: 'currency',
      currency,
    }).format(numericAmount);
  } catch {
    return `${numericAmount.toFixed(2)} ${currency}`;
  }
};

const formatAmount = computed(() => {
  const amountDisplay = pick('amount_display', 'amountDisplay');
  if (amountDisplay) return amountDisplay;

  const amount = pick('amount');
  const total = pick('total', 'total_amount', 'totalAmount');
  const fee = pick('fee', 'processing_fee', 'processingFee');
  const amountToFormat =
    isPresent(amount) || !isPresent(total)
      ? amount
      : Number(total) - Number(fee || 0);

  return formatMoney(amountToFormat);
});

const formatDate = value => {
  if (!isPresent(value)) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat(locale.value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  }).format(date);
};

const isWidget = computed(() => props.variant === 'widget');

const detailRows = computed(() =>
  [
    {
      label: translate('SUBSCRIPTION_ID'),
      value: pick('subscription_id', 'subscriptionId'),
      dashboardOnly: true,
    },
    {
      label: translate('PLAN'),
      value: pick('plan'),
    },
    {
      label: translate('ORDER_ID'),
      value: pick('order_id', 'orderId'),
    },
    {
      label: translate('PERIOD_START'),
      value: formatDate(
        pick(
          'period_start',
          'periodStart',
          'current_period_start',
          'currentPeriodStart'
        )
      ),
    },
    {
      label: translate('PERIOD_END'),
      value: formatDate(
        pick(
          'period_end',
          'periodEnd',
          'current_period_end',
          'currentPeriodEnd'
        )
      ),
    },
    {
      label: translate('PAYMENT_METHOD'),
      value: pick('payment_method', 'paymentMethod'),
    },
    {
      label: translate('PROCESSED_ON'),
      value: formatDate(pick('processed_at', 'processedAt')),
    },
    {
      label: translate('ARRIVAL'),
      value: pick('estimated_arrival', 'estimatedArrival'),
    },
    {
      label: translate('FEE'),
      value: formatMoney(pick('fee', 'processing_fee', 'processingFee')),
    },
  ].filter(
    row => isPresent(row.value) && (!row.dashboardOnly || !isWidget.value)
  )
);

const itemRows = computed(() => {
  const items = pick('items') || [];
  if (!Array.isArray(items)) return [];

  return items
    .map(item => ({
      label: pickFrom(item, 'label', 'title', 'name'),
      value: pickFrom(item, 'value', 'amount', 'description'),
    }))
    .filter(row => isPresent(row.label) && isPresent(row.value));
});

const note = computed(() => pick('note', 'description'));
</script>

<template>
  <article
    class="w-full max-w-[25rem] overflow-hidden shadow-sm"
    :class="
      isWidget
        ? 'chat-bubble agent !p-0 rounded-lg bg-[#FFFFFF12] text-white'
        : 'rounded-lg border border-n-weak bg-n-background text-n-slate-12 dark:bg-n-solid-3'
    "
  >
    <div>
      <div class="px-4 py-4">
        <div class="flex items-center gap-3">
          <span
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
            :class="isWidget ? 'bg-[#064E3B]' : 'bg-n-teal-3'"
          >
            <svg
              class="h-5 w-5"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M16.25 6.25L8.75 13.75L5 10"
                :stroke="isWidget ? '#6EE7B7' : 'currentColor'"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <circle
                cx="10"
                cy="10"
                r="8"
                :stroke="isWidget ? '#6EE7B7' : 'currentColor'"
                stroke-width="1.5"
              />
            </svg>
          </span>
          <div class="min-w-0 flex-1">
            <h3
              class="text-base font-semibold leading-6"
              :class="isWidget ? 'text-white' : 'text-n-slate-12'"
            >
              {{ translate('TITLE') }}
            </h3>
          </div>
        </div>
        <p
          v-if="content"
          v-dompurify-html="formatMessage(content, false)"
          class="prose prose-sm prose-bubble mt-3"
          :class="isWidget ? 'text-slate-300' : 'text-n-slate-11'"
        />
      </div>

      <div
        v-if="formatAmount"
        class="mx-4 rounded-lg px-4 py-3"
        :class="isWidget ? 'bg-white/5' : 'bg-n-slate-2'"
      >
        <div class="flex items-center justify-between gap-4">
          <span
            class="text-sm"
            :class="isWidget ? 'text-slate-300' : 'text-n-slate-11'"
          >
            {{ translate('AMOUNT') }}
          </span>
          <strong
            class="text-xl font-semibold tabular-nums"
            :class="isWidget ? 'text-white' : 'text-n-slate-12'"
          >
            {{ formatAmount }}
          </strong>
        </div>
      </div>

      <dl
        v-if="detailRows.length || itemRows.length"
        class="mx-4 mt-4 space-y-3"
      >
        <div
          v-for="row in detailRows"
          :key="row.label"
          class="flex items-start justify-between gap-4 text-sm"
        >
          <dt
            class="shrink-0"
            :class="isWidget ? 'text-slate-400' : 'text-n-slate-11'"
          >
            {{ row.label }}
          </dt>
          <dd
            class="min-w-0 break-words text-right font-medium"
            :class="isWidget ? 'text-slate-100' : 'text-n-slate-12'"
          >
            {{ row.value }}
          </dd>
        </div>
        <div
          v-for="row in itemRows"
          :key="row.label"
          class="flex items-start justify-between gap-4 text-sm"
        >
          <dt
            class="shrink-0"
            :class="isWidget ? 'text-slate-400' : 'text-n-slate-11'"
          >
            {{ row.label }}
          </dt>
          <dd
            class="min-w-0 break-words text-right font-medium"
            :class="isWidget ? 'text-slate-100' : 'text-n-slate-12'"
          >
            {{ row.value }}
          </dd>
        </div>
      </dl>

      <p
        v-if="note"
        class="px-4 pt-4 text-sm leading-5"
        :class="isWidget ? 'text-slate-300' : 'text-n-slate-11'"
      >
        {{ note }}
      </p>

      <div
        class="mt-4 px-4 pb-3 pt-2"
        :class="isWidget ? 'bg-white/5' : 'bg-n-slate-2'"
      >
        <div
          class="flex items-center gap-2 text-xs leading-5"
          :class="isWidget ? 'text-slate-300' : 'text-n-slate-11'"
        >
          <span
            class="i-lucide-shield-check h-4 w-4"
            :class="isWidget ? 'text-emerald-300' : 'text-n-teal-11'"
          />
          <span>{{ translate('FOOTER') }}</span>
        </div>
      </div>
    </div>
  </article>
</template>
